import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaEnvelope, FaLock, FaArrowLeft, FaCheck, FaTimes } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useMsal } from '@azure/msal-react';
import { apiService, UserProfile } from '../services/api';
import styles from './UserSettingsPage.module.css';

const UserSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { instance, accounts } = useMsal();
  
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Initialize profile with user data from context immediately
  useEffect(() => {
    if (user && !profile) {
      console.log('UserSettingsPage: Initializing profile with context user data');
      setProfile({
        displayName: user.name || 'Not available',
        mail: user.email || 'Not available',
        userPrincipalName: user.email || 'Not available',
      });
    }
  }, [user, profile]);

  // Debug: Track profile state changes
  React.useEffect(() => {
    console.log('UserSettingsPage: Profile state changed:', { profile, timestamp: new Date().toISOString() });
  }, [profile]);

  // Fetch user profile from Microsoft Graph API
  useEffect(() => {
    const fetchUserProfile = async () => {
      console.log('UserSettingsPage: Starting to fetch user profile');
      console.log('UserSettingsPage: Available accounts:', accounts.length);
      console.log('UserSettingsPage: User from context:', user);
      
      if (accounts.length === 0) {
        console.log('UserSettingsPage: No accounts available, using context user data');
        // Use user data from context immediately
        setProfile({
          displayName: user?.name || 'Not available',
          mail: user?.email || 'Not available',
          userPrincipalName: user?.email || 'Not available',
        });
        return;
      }
      
      setIsLoading(true);
      try {
        console.log('UserSettingsPage: Attempting to get token for Graph API');
        const tokenRequest = {
          scopes: ['https://graph.microsoft.com/User.Read'],
          account: accounts[0],
        };

        const tokenResponse = await instance.acquireTokenSilent(tokenRequest);
        console.log('UserSettingsPage: Token acquired, calling Graph API');
        
        const response = await apiService.getUserProfile(tokenResponse.accessToken);
        console.log('UserSettingsPage: Graph API response:', response);
        
        if (response.data) {
          console.log('UserSettingsPage: Using Graph API data');
          setProfile({
            displayName: response.data.displayName || user?.name || 'Not available',
            mail: response.data.mail || response.data.userPrincipalName || user?.email || 'Not available',
            userPrincipalName: response.data.userPrincipalName || user?.email || 'Not available',
          });
        } else {
          console.error('UserSettingsPage: Failed to fetch user profile:', response.error);
          // Fallback to user data from context
          console.log('UserSettingsPage: Falling back to context user data');
          setProfile({
            displayName: user?.name || 'Not available',
            mail: user?.email || 'Not available',
            userPrincipalName: user?.email || 'Not available',
          });
        }
      } catch (error) {
        console.error('UserSettingsPage: Error fetching user profile:', error);
        // Fallback to user data from context
        console.log('UserSettingsPage: Error occurred, falling back to context user data');
        setProfile({
          displayName: user?.name || 'Not available',
          mail: user?.email || 'Not available',
          userPrincipalName: user?.email || 'Not available',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserProfile();
  }, [accounts, instance, user]);

  const validatePasswordForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters long';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validatePasswordForm()) {
      return;
    }

    setIsUpdatingPassword(true);
    setMessage(null);

    try {
      if (accounts.length === 0) {
        throw new Error('No authenticated account found');
      }

      const tokenRequest = {
        scopes: ['https://graph.microsoft.com/User.Read'],
        account: accounts[0],
      };

      const tokenResponse = await instance.acquireTokenSilent(tokenRequest);
      
      const response = await apiService.updateUserPassword(tokenResponse.accessToken, {
        currentPassword: '', // Not used in Graph API
        newPassword: newPassword,
      });

      if (response.error) {
        setMessage({
          type: 'error',
          text: response.error
        });
      } else {
        setMessage({
          type: 'success',
          text: 'Password updated successfully!'
        });
        // Clear form
        setNewPassword('');
        setConfirmPassword('');
        setErrors({});
      }

    } catch (error) {
      console.error('Password update failed:', error);
      setMessage({
        type: 'error',
        text: 'Failed to update password. Please try again or contact support.'
      });
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (name === 'newPassword') {
      setNewPassword(value);
    } else if (name === 'confirmPassword') {
      setConfirmPassword(value);
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleBackToHome = () => {
    navigate('/home');
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className={styles.settingsPage}>
      <div className={styles.background}>
        <div className={styles.overlay}></div>
      </div>
      
      <div className={styles.content}>
        <div className={styles.header}>
          <button 
            className={styles.backButton}
            onClick={handleBackToHome}
            title="Back to Home"
          >
            <FaArrowLeft />
          </button>
          <h1 className={styles.pageTitle}>User Settings</h1>
          <button 
            className={styles.logoutButton}
            onClick={handleLogout}
            title="Sign out"
          >
            <FaTimes />
          </button>
        </div>

        <div className={styles.settingsContainer}>
          {/* Debug Information */}
          {/* <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Debug Information</h2>
            <div className={styles.profileInfo}>
              <div className={styles.infoItem}>
                <div className={styles.infoContent}>
                  <label className={styles.infoLabel}>User from Context</label>
                  <div className={`${styles.infoValue} ${styles.debugInfo}`}>
                    <pre>{JSON.stringify(user, null, 2)}</pre>
                  </div>
                </div>
              </div>
              <div className={styles.infoItem}>
                <div className={styles.infoContent}>
                  <label className={styles.infoLabel}>Profile State</label>
                  <div className={`${styles.infoValue} ${styles.debugInfo}`}>
                    <pre>{JSON.stringify(profile, null, 2)}</pre>
                  </div>
                </div>
              </div>
              <div className={styles.infoItem}>
                <div className={styles.infoContent}>
                  <label className={styles.infoLabel}>Loading State</label>
                  <div className={styles.infoValue}>
                    {isLoading ? 'Loading...' : 'Not loading'}
                  </div>
                </div>
              </div>
            </div>
          </div> */}

          {/* Profile Information Section */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Profile Information</h2>
            <div className={styles.profileInfo}>
              <div className={styles.infoItem}>
                <div className={styles.infoIcon}>
                  <FaUser />
                </div>
                <div className={styles.infoContent}>
                  <label className={styles.infoLabel}>Display Name</label>
                  <div className={styles.infoValue}>
                    {isLoading ? 'Loading...' : (profile?.displayName || 'Not available')}
                  </div>
                </div>
              </div>

              <div className={styles.infoItem}>
                <div className={styles.infoIcon}>
                  <FaEnvelope />
                </div>
                <div className={styles.infoContent}>
                  <label className={styles.infoLabel}>Email</label>
                  <div className={styles.infoValue}>
                    {isLoading ? 'Loading...' : (profile?.mail || profile?.userPrincipalName || 'Not available')}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Password Update Section */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Change Password</h2>
            
            {message && (
              <div className={`${styles.message} ${styles[message.type]}`}>
                {message.type === 'success' ? <FaCheck /> : <FaTimes />}
                {message.text}
              </div>
            )}

            <form onSubmit={handlePasswordUpdate} className={styles.passwordForm}>
              <div className={styles.inputGroup}>
                <div className={styles.inputWrapper}>
                  <FaLock className={styles.inputIcon} />
                  <input
                    type="password"
                    name="newPassword"
                    placeholder="New Password"
                    value={newPassword}
                    onChange={handleInputChange}
                    className={`${styles.input} ${errors.newPassword ? styles.inputError : ''}`}
                    disabled={isUpdatingPassword}
                  />
                </div>
                {errors.newPassword && <span className={styles.errorText}>{errors.newPassword}</span>}
              </div>

              <div className={styles.inputGroup}>
                <div className={styles.inputWrapper}>
                  <FaLock className={styles.inputIcon} />
                  <input
                    type="password"
                    name="confirmPassword"
                    placeholder="Confirm New Password"
                    value={confirmPassword}
                    onChange={handleInputChange}
                    className={`${styles.input} ${errors.confirmPassword ? styles.inputError : ''}`}
                    disabled={isUpdatingPassword}
                  />
                </div>
                {errors.confirmPassword && <span className={styles.errorText}>{errors.confirmPassword}</span>}
              </div>

              <button
                type="submit"
                className={styles.updateButton}
                disabled={isUpdatingPassword}
              >
                {isUpdatingPassword ? 'Updating...' : 'Update Password'}
              </button>
            </form>

            <div className={styles.passwordNote}>
              <p>
                <strong>Note:</strong> Password updates require administrative privileges. 
                Please contact your system administrator or use the Azure portal to change your password.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSettingsPage;
